// Pure snapshot diff between two ArchVersion snapshots. Used by both the
// version-restore preview and the "Diff vX vs vY" lens in the right panel.
//
// Two snapshots are objects shaped like:
//   { nodes: ArchNode[], peerConnections: PeerConnection[] }
//
// Result:
//   {
//     nodesAdded:   ArchNode[],          // present in B, missing in A
//     nodesRemoved: ArchNode[],          // present in A, missing in B
//     nodesRenamed: { id, before, after, beforeName, afterName }[],
//     nodesMoved:   { id, name, beforeParentId, afterParentId }[],
//     peersAdded:   PeerConnection[],
//     peersRemoved: PeerConnection[],
//   }

export function diffSnapshots(a, b) {
  const aNodes = (a?.nodes || []);
  const bNodes = (b?.nodes || []);
  const aMap = new Map(aNodes.map((n) => [n.id, n]));
  const bMap = new Map(bNodes.map((n) => [n.id, n]));

  const nodesAdded = bNodes.filter((n) => !aMap.has(n.id));
  const nodesRemoved = aNodes.filter((n) => !bMap.has(n.id));

  const nodesRenamed = [];
  const nodesMoved = [];
  for (const n of bNodes) {
    const prev = aMap.get(n.id);
    if (!prev) continue;
    if (prev.name !== n.name) {
      nodesRenamed.push({
        id: n.id,
        beforeName: prev.name,
        afterName: n.name,
        type: n.type,
      });
    }
    if (prev.parentId !== n.parentId) {
      nodesMoved.push({
        id: n.id,
        name: n.name,
        beforeParentId: prev.parentId,
        afterParentId: n.parentId,
        beforeParentName: prev.parentId ? aMap.get(prev.parentId)?.name : "—",
        afterParentName: n.parentId ? bMap.get(n.parentId)?.name : "—",
      });
    }
  }

  const aPeerMap = new Map((a?.peerConnections || []).map((p) => [p.id, p]));
  const bPeerMap = new Map((b?.peerConnections || []).map((p) => [p.id, p]));
  const peersAdded = (b?.peerConnections || []).filter((p) => !aPeerMap.has(p.id));
  const peersRemoved = (a?.peerConnections || []).filter((p) => !bPeerMap.has(p.id));

  return {
    nodesAdded,
    nodesRemoved,
    nodesRenamed,
    nodesMoved,
    peersAdded,
    peersRemoved,
  };
}

/** Total number of changes — useful for badge counts. */
export function diffChangeCount(diff) {
  if (!diff) return 0;
  return (
    diff.nodesAdded.length +
    diff.nodesRemoved.length +
    diff.nodesRenamed.length +
    diff.nodesMoved.length +
    diff.peersAdded.length +
    diff.peersRemoved.length
  );
}
